import React, {
  useEffect, useRef, useState, forwardRef, useImperativeHandle,
} from 'react';
import {
  MediaPlayer,
  MediaProvider,
  MediaPlayerInstance,
} from '@vidstack/react';
import {
  IconButton, SxProps, Theme, Box, Modal, Typography,
} from '@mui/material';
import { PlyrLayout, plyrLayoutIcons } from '@vidstack/react/player/layouts/plyr';
import { BoldPlayIcon, CloseSquareIcon } from 'assets/icons';

import { convertToHLSUrl } from './utils';
import styles from './plyr-custom.module.css';

export interface StreamingPlayerRef {
  play: () => void;
  pause: () => void;
  openModal: () => void;
  closeModal: () => void;
}

interface StreamingPlayerProps {
  src?: string;
  title?: string;
  poster?: string | undefined;
  openInModal?: boolean;
  autoPlay?: boolean;
  controls?: boolean;
  loop?: boolean;
  muted?: boolean;
  aspectRatio?: string;
  crossOrigin?: 'anonymous' | 'use-credentials';
  playsInline?: boolean;
  sx?: SxProps<Theme> | undefined;
  noPlayButton?: boolean;
  handleModelExternally?: boolean;
  onRemove?: () => void;
}

const StreamingPlayer = forwardRef<StreamingPlayerRef, StreamingPlayerProps>(({
  src,
  title,
  poster,
  openInModal = false,
  autoPlay = false,
  controls = false,
  loop = false,
  muted = false,
  aspectRatio = '16/9',
  crossOrigin = 'anonymous',
  playsInline = true,
  sx = {},
  noPlayButton = false,
  handleModelExternally = false,
  onRemove,
}, ref) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const playerRef = useRef<MediaPlayerInstance>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);

  const [wrapperSize, setWrapperSize] = useState({ width: 0, height: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!wrapperRef.current) return undefined;

    const resizeObserver = new ResizeObserver((entries) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setWrapperSize({ width, height });
      }
    });

    resizeObserver.observe(wrapperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const getIconSize = () => {
    const { width, height } = wrapperSize;
    const minDimension = Math.min(width, height);

    if (minDimension <= 50) return 20;
    if (minDimension <= 100) return 30;
    if (minDimension <= 200) return 40;
    return 55;
  };

  const handlePlay = async () => {
    try {
      if (playerRef.current) {
        if (playerRef.current.$state && playerRef.current.$state.canPlay()) {
          await playerRef.current.play();
        }
      }
    } catch (playError) {
      // eslint-disable-next-line no-console
      console.error('Error playing video:', playError);
    }
  };

  const handleOpenModal = async () => {
    setIsModalOpen(true);
    await new Promise((resolve) => {
      setTimeout(resolve, 100);
    });
    await handlePlay();
  };

  const handleCloseModal = (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.stopPropagation();
    if (playerRef.current) {
      playerRef.current.pause();
    }
    setIsModalOpen(false);
  };

  const handleThumbnailClick = () => {
    if (!handleModelExternally) {
      handleOpenModal();
    }
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove();
    }
  };

  useImperativeHandle(ref, () => ({
    play: async () => {
      await handlePlay();
    },
    pause: () => {
      if (playerRef.current) {
        playerRef.current.pause();
      }
    },
    openModal: () => {
      handleOpenModal();
    },
    closeModal: () => {
      handleCloseModal();
    },
  }), []);

  // Convert source to HLS URL if possible
  useEffect(() => {
    const loadSource = async () => {
      if (src) {
        try {
          const convertedSrc = await convertToHLSUrl(src);
          setVideoSrc(convertedSrc);
          setIsLoading(false);
        } catch (loadError) {
          setIsLoading(true);
          setError('Failed to load video source');
        }
      } else {
        setVideoSrc(null);
      }
    };

    loadSource();
  }, [src]);

  // Render the video player
  const renderPlayer = () => (
    <div className={styles.root}>
      <MediaPlayer
        ref={playerRef}
        src={videoSrc || ''}
        title={title}
        poster={poster}
        aspectRatio={aspectRatio}
        autoPlay={autoPlay || openInModal}
        controls={controls}
        crossOrigin={crossOrigin}
        loop={loop}
        muted={muted}
        playsInline={playsInline}
        load="eager"
        onLoadStart={() => setIsLoading(true)}
        onLoadedData={() => setIsLoading(false)}
        onError={() => {
          setError('Error playing video');
          setIsLoading(true);
        }}
        className={styles.customPlayer}
      >
        <MediaProvider />
        <PlyrLayout
          icons={plyrLayoutIcons}
          slots={{
            playLargeButton:
              noPlayButton
                ? null
                : (
                  <BoldPlayIcon
                    sx={{
                      fontSize: 55,
                      color: (t) => t.palette.secondary.light,
                    }}
                    className="custom-play-icon"
                  />
                ),
          }}
          className={styles.plyrLayout}
        />
      </MediaPlayer>
    </div>
  );

  // Render the thumbnail with play button for modal mode
  const renderThumbnail = () => (
    <Box
      ref={wrapperRef}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        ...sx,
      }}
      onClick={handleThumbnailClick}
    >
      <Box
        component={poster ? 'img' : 'video'}
        src={poster || src || ''}
        alt={title || 'Video'}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
        {...(!poster && {
          autoPlay: false,
          muted: true,
          playsInline: true,
          controls: false,
          preload: 'metadata',
        })}
      />
      {onRemove && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
          }}
          onClick={handleRemoveClick}
        >
          <IconButton
            sx={{
              padding: 0,
              borderRadius: 0,
            }}
          >
            <CloseSquareIcon
              sx={{
                fontSize: 30,
                color: 'background.paper',
                borderRadius: '50%',
                backgroundColor: '#00000036',
              }}
            />
          </IconButton>
        </Box>
      )}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
        }}
      >
        <BoldPlayIcon
          sx={{
            fontSize: getIconSize(),
            color: (t) => t.palette.secondary.light,
          }}
          className="custom-play-icon"
        />
      </Box>
    </Box>
  );

  // Render modal with player
  const renderModal = () => (
    <Modal
      open={isModalOpen}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          handleCloseModal();
        }
      }}
      onClick={(e) => e.stopPropagation()} // Stop click propagation at Modal level
      aria-labelledby="video-modal"
      aria-describedby="video-modal-description"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        },
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'relative',
          width: '90%',
          maxWidth: '1200px',
        }}
      >
        {renderPlayer()}
      </Box>
    </Modal>
  );

  if (openInModal) {
    return (
      <>
        {renderThumbnail()}
        {renderModal()}
      </>
    );
  }

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      {isLoading && !error && (
        <Box
          data-testid="streaming-player-loading"
          display="flex"
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        >
          <div className={styles.bufferingIndicator} />
          <Typography fontWeight="medium" variant="subtitle1" sx={{ color: 'white', mt: 2 }}>
            Media is loading..
          </Typography>
        </Box>
      )}

      {error && (
        <Box
          data-testid="streaming-player-error"
          display="flex"
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        >
          <Typography fontWeight="medium" variant="subtitle1" sx={{ color: 'white' }}>
            {error}
          </Typography>
        </Box>
      )}
      {renderPlayer()}
    </Box>
  );
});

StreamingPlayer.displayName = 'StreamingPlayer';

StreamingPlayer.defaultProps = {
  src: undefined,
  title: undefined,
  poster: undefined,
  openInModal: false,
  autoPlay: false,
  controls: false,
  loop: false,
  muted: false,
  aspectRatio: '16/9',
  crossOrigin: 'anonymous',
  playsInline: true,
  sx: {},
  noPlayButton: false,
  handleModelExternally: false,
  onRemove: undefined,
};

export default StreamingPlayer;
